import type { LeadState, SessionInfo, Variant, Audience, StepId } from '../types'

interface SessionResponse {
  sessionId: string
  variant: Variant
  audience: Audience
}

export async function ensureSession(client: SessionInfo, utm: UTMs): Promise<SessionInfo> {
  try {
    const res = await fetch('/api/session', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        sessionId: client.sessionId,
        // Always send `hint: 'chat'` from the chat widget — never read from
        // client.variant (which is sourced from cookie and may be stale from
        // a prior /<audience>/beregner visit). Backend prioritises body.hint
        // over cookie when creating fresh sessions, so this guarantees the
        // session row is tagged variant=chat.
        hint: 'chat',
        audience: client.audience,
        ...utm,
      }),
    })
    if (!res.ok) return client
    const data = (await res.json()) as SessionResponse
    return {
      sessionId: data.sessionId,
      variant: data.variant,
      audience: data.audience ?? client.audience,
    }
  } catch {
    return client
  }
}

export interface UTMs {
  utm_source?: string
  utm_medium?: string
  utm_campaign?: string
  utm_content?: string
  utm_term?: string
  utm_id?: string
  utm_adgroup?: string
  fbclid?: string
  // Google Ads + Microsoft Bing click IDs — capture'd på landing, persisteres
  // i D1 leads-tabellen (migration 0005) og forwardes til Zapier → Adversus.
  // Adversus inkluderer dem i Purchase-eventet ved closed deal → Stape sGTM
  // tag for Google Ads Offline Conversion + Bing UET Offline kan attribuere
  // den lukkede deal tilbage til det oprindelige ad-klik.
  gclid?: string
  msclkid?: string
  wbraid?: string
  gbraid?: string
  gclsrc?: string
  referer?: string
}

export function readUTMs(): UTMs {
  if (typeof window === 'undefined') return {}
  const p = new URLSearchParams(window.location.search)
  return {
    utm_source: p.get('utm_source') ?? undefined,
    utm_medium: p.get('utm_medium') ?? undefined,
    utm_campaign: p.get('utm_campaign') ?? undefined,
    utm_content: p.get('utm_content') ?? undefined,
    utm_term: p.get('utm_term') ?? undefined,
    utm_id: p.get('utm_id') ?? undefined,
    utm_adgroup: p.get('utm_adgroup') ?? undefined,
    // fbclid sender vi til server-siden så den kan inferre source=facebook/medium=cpc
    // når Meta-ad URL'en mangler utm-tagging. Backend's inferSourceMedium() bruger
    // den kun hvis utm_source er tom — eksplicit UTM vinder altid.
    fbclid: p.get('fbclid') ?? undefined,
    // Google Ads + Bing click IDs — persisteres på leadet så Adversus kan
    // forwarde dem i Purchase-event ved closed deal for Offline Conversion
    // Import attribution.
    gclid: p.get('gclid') ?? undefined,
    msclkid: p.get('msclkid') ?? undefined,
    wbraid: p.get('wbraid') ?? undefined,
    gbraid: p.get('gbraid') ?? undefined,
    gclsrc: p.get('gclsrc') ?? undefined,
    referer: document.referrer || undefined,
  }
}

export function logEvent(sessionId: string, step: StepId, eventType: 'view' | 'submit' | 'abandon') {
  const payload = JSON.stringify({ sessionId, step, eventType, t: Date.now() })
  // sendBeacon survives page unload — critical for 'abandon' events
  if (typeof navigator !== 'undefined' && navigator.sendBeacon) {
    const blob = new Blob([payload], { type: 'application/json' })
    if (navigator.sendBeacon('/api/event', blob)) return
  }
  fetch('/api/event', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: payload,
    keepalive: true,
  }).catch(() => {})
}

export interface SubmitLeadPayload extends LeadState {
  sessionId: string
  variant: Variant
  audience: Audience
  consentTextRequired: string
  utm: UTMs
  _hp_email?: string  // honeypot — must be empty
  // Meta-attribution-felter — læses i Widget.tsx (readFbParams + URL-params)
  // og persisteres i leads-tabellen så Zapier-worker kan forwarde dem top-
  // level til CRM. Optional fordi gamle widget-builds (cached i browser før
  // denne deploy) kan stadig være på vej til /api/lead uden FB-felter.
  fbp?: string
  fbc?: string
  fbclid?: string
  fb_campaign_id?: string
  fb_adset_id?: string
  fb_ad_id?: string
  fb_campaign_name?: string
  fb_adset_name?: string
  fb_placement?: string
  fb_platform?: string
  // TikTok-attribution-felter — parallel til fb_*-felterne ovenfor. Læses i
  // Widget.tsx (readTtParams) fra ?ttclid + ?tt_*-URL-makroer + _ttp-cookie og
  // persisteres i leads-tabellen så queue-worker kan forwarde dem til Zapier
  // OG fyre en server-side conversion til TikTok Events API (CAPI). Optional
  // af samme grund som fb_*: cached widget-builds før denne deploy sender dem
  // ikke, og server skal stadig acceptere requesten uden at fejle.
  ttclid?: string
  ttp?: string
  tt_campaign_id?: string
  tt_adgroup_id?: string
  tt_ad_id?: string
  tt_creative_id?: string
  tt_campaign_name?: string
  tt_adgroup_name?: string
  tt_placement?: string
  // Google Ads + Microsoft Bing click identifiers — top-level på payloaden
  // så /api/lead-handler kan persistere dem direkte i leads-tabellen og queue.ts
  // kan forwarde dem til Zapier → Adversus. Adversus inkluderer dem i Purchase-
  // event ved closed deal så Google Ads + Bing Offline Conversion Import kan
  // matche den lukkede deal tilbage til det oprindelige ad-klik.
  gclid?: string
  msclkid?: string
  wbraid?: string
  gbraid?: string
  gclsrc?: string
  // Berigelsesfelter til lead_payload-harmonisering med calculator-flowet:
  // server bruger disse til at bygge svar/beregning/meta nested-strukturen
  // som Zapier-worker spreader oven på chat-payloaden, så CRM ser samme
  // contract uanset variant. Alle optional → ældre cached widget-builds
  // sender dem ikke, og lead_payload er da blot null (graceful degradation).
  completion_seconds?: number
  device?: 'mobile' | 'desktop' | 'tablet'
  page_url?: string
  referrer?: string
  lead_score?: number
  estimeret_aarlig_praemie?: number
  besparelse_low?: number
  besparelse_high?: number
}

export async function submitLead(payload: SubmitLeadPayload): Promise<{ ok: boolean; error?: string }> {
  try {
    const res = await fetch('/api/lead', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      keepalive: true,
    })
    if (!res.ok) {
      const txt = await res.text().catch(() => '')
      // Stash for retry on next visit
      try {
        localStorage.setItem('leadflow_pending_lead', JSON.stringify(payload))
      } catch {}
      return { ok: false, error: `HTTP ${res.status}: ${txt.slice(0, 200)}` }
    }
    try { localStorage.removeItem('leadflow_pending_lead') } catch {}
    return { ok: true }
  } catch (err) {
    try {
      localStorage.setItem('leadflow_pending_lead', JSON.stringify(payload))
    } catch {}
    return { ok: false, error: err instanceof Error ? err.message : String(err) }
  }
}

/* ─── Admin endpoints (password protected) ─── */

export interface KPIData {
  sessions: number
  leads: number
  completionRate: number
  leadsLast24h: number
  prev: { sessions: number; leads: number; completionRate: number }
}

export interface FunnelRow {
  step: string
  views: number
  submits: number
  abandons: number
  // Sat når et step springes over for nogle brugere baseret på tidligere svar
  // (fx unge-beregneren springer maanedlig_praemie + selskab over hvis brugeren
  // ikke har bil i dag). Frontend renderer disse rows med "BETINGET"-badge +
  // "X sprang over (årsag)" i stedet for at vise det som drop-off, så funnel'en
  // ikke fremstår matematisk umulig (senere step > tidligere step).
  conditional?: boolean
  skippedHere?: number
  skipReason?: string
}

export interface VariantStats {
  variant: Variant
  sessions: number
  completions: number
  rate: number
  ciLow: number
  ciHigh: number
  medianTimeMs: number | null
}

export interface VariantsResponse {
  variants: VariantStats[]
  pValue: number | null
  significant: boolean
  sampleSizeNeeded: number | null
  topDropoff: Record<Variant, { step: string; rate: number } | null>
}

export interface LeadRow {
  id: string
  variant: Variant
  audience: Audience
  firstName: string
  lastName: string
  phone: string
  postnr: string | null
  by: string | null
  age: string | null
  insurances: string | null
  hasExisting: number
  consentMarketing: number
  createdAt: number
  zapierStatus: 'pending' | 'sent' | 'failed'
}

export interface LeadsResponse {
  rows: LeadRow[]
  total: number
}

export interface DailyRow { day: string; sessions: number; leads: number }
export interface HourlyRow { hour: number; n: number }
export interface DeviceStats { sessions: number; leads: number }
export interface DevicesResponse { buckets: { desktop: DeviceStats; mobile: DeviceStats; tablet: DeviceStats } }
export interface TimeStat { count: number; medianMs: number | null; avgMs: number | null }
export interface TimespentResponse { timeToLead: TimeStat; timeToAbandon: TimeStat }
export interface DropoffRow { step: string; n: number }

interface DateRange { from: number; to: number }

/**
 * Append both flow dimensions to a query: `variant` (chat vs calculator A/B)
 * and `audience` (unge vs aeldre target segment). Either is optional so admin
 * pages can slice independently along each axis.
 */
function withFlow(params: URLSearchParams, variant?: Variant, audience?: Audience): URLSearchParams {
  if (variant) params.set('variant', variant)
  if (audience) params.set('audience', audience)
  return params
}

function rangeParams(range: DateRange, variant?: Variant, audience?: Audience): URLSearchParams {
  return withFlow(new URLSearchParams({ from: String(range.from), to: String(range.to) }), variant, audience)
}

export async function fetchStats(range: DateRange, variant?: Variant, audience?: Audience): Promise<KPIData> {
  const r = await fetch(`/api/admin/stats?${rangeParams(range, variant, audience)}`, { credentials: 'include' })
  if (!r.ok) throw new Error(`stats ${r.status}`)
  return r.json()
}

export async function fetchFunnel(range: DateRange, variant: Variant, audience?: Audience): Promise<{ rows: FunnelRow[]; activeExcluded?: number; resumeLeads?: number }> {
  const r = await fetch(`/api/admin/funnel?${rangeParams(range, variant, audience)}`, { credentials: 'include' })
  if (!r.ok) throw new Error(`funnel ${r.status}`)
  return r.json()
}

export async function fetchVariants(range: DateRange, audience?: Audience): Promise<VariantsResponse> {
  const params = withFlow(new URLSearchParams({ from: String(range.from), to: String(range.to) }), undefined, audience)
  const r = await fetch(`/api/admin/variants?${params}`, { credentials: 'include' })
  if (!r.ok) throw new Error(`variants ${r.status}`)
  return r.json()
}

export async function fetchDaily(range: DateRange, variant?: Variant, audience?: Audience): Promise<{ days: DailyRow[] }> {
  const r = await fetch(`/api/admin/daily?${rangeParams(range, variant, audience)}`, { credentials: 'include' })
  if (!r.ok) throw new Error(`daily ${r.status}`)
  return r.json()
}

export async function fetchHourly(range: DateRange, variant?: Variant, audience?: Audience): Promise<{ hours: HourlyRow[] }> {
  const r = await fetch(`/api/admin/hourly?${rangeParams(range, variant, audience)}`, { credentials: 'include' })
  if (!r.ok) throw new Error(`hourly ${r.status}`)
  return r.json()
}

export async function fetchDevices(range: DateRange, variant?: Variant, audience?: Audience): Promise<DevicesResponse> {
  const r = await fetch(`/api/admin/devices?${rangeParams(range, variant, audience)}`, { credentials: 'include' })
  if (!r.ok) throw new Error(`devices ${r.status}`)
  return r.json()
}

export async function fetchTimespent(range: DateRange, variant?: Variant, audience?: Audience): Promise<TimespentResponse> {
  const r = await fetch(`/api/admin/timespent?${rangeParams(range, variant, audience)}`, { credentials: 'include' })
  if (!r.ok) throw new Error(`timespent ${r.status}`)
  return r.json()
}

export async function fetchDropoff(range: DateRange, variant?: Variant, audience?: Audience): Promise<{ rows: DropoffRow[] }> {
  const r = await fetch(`/api/admin/dropoff?${rangeParams(range, variant, audience)}`, { credentials: 'include' })
  if (!r.ok) throw new Error(`dropoff ${r.status}`)
  return r.json()
}

export async function fetchLeads(range: DateRange, page = 1, pageSize = 25, variant?: Variant, audience?: Audience): Promise<LeadsResponse> {
  const params = rangeParams(range, variant, audience)
  params.set('page', String(page))
  params.set('pageSize', String(pageSize))
  const r = await fetch(`/api/admin/leads?${params}`, { credentials: 'include' })
  if (!r.ok) throw new Error(`leads ${r.status}`)
  return r.json()
}

export function leadsCsvUrl(range: DateRange, variant?: Variant, audience?: Audience): string {
  return `/api/admin/leads.csv?${rangeParams(range, variant, audience)}`
}

export interface RetryScope {
  from: number
  to: number
  variant: Variant | null
  audience: Audience | null
}

/**
 * Re-queuer alle failed leads inden for det nuværende admin-filter (range +
 * variant + audience). Hvis ingen filter passes, falder backend tilbage til
 * en 30-dages window — failed leads ældre end det er typisk permanente
 * Zapier-konfigurationsfejl som re-queue ikke løser.
 */
export async function retryFailedLeads(
  range: DateRange,
  variant?: Variant,
  audience?: Audience,
): Promise<{ requeued: number; scope: RetryScope }> {
  const r = await fetch(`/api/admin/retry-failed?${rangeParams(range, variant, audience)}`, {
    method: 'POST',
    credentials: 'include',
  })
  if (!r.ok) throw new Error(`retry ${r.status}`)
  return r.json()
}

export async function deleteLead(id: string): Promise<void> {
  const r = await fetch(`/api/admin/leads/${id}`, { method: 'DELETE', credentials: 'include' })
  if (!r.ok) throw new Error(`delete ${r.status}`)
}

/* ─── v2 endpoints ─── */

export interface AcquisitionRow {
  source: string
  medium: string
  campaign: string
  sessions: number
  leads: number
  conversionRate: number
}
export interface AcquisitionResponse {
  rows: AcquisitionRow[]
  totals: { sessions: number; leads: number; conversionRate: number }
}

export async function fetchAcquisition(range: DateRange, variant?: Variant, audience?: Audience): Promise<AcquisitionResponse> {
  const r = await fetch(`/api/admin/acquisition?${rangeParams(range, variant, audience)}`, { credentials: 'include' })
  if (!r.ok) throw new Error(`acquisition ${r.status}`)
  return r.json()
}

export interface LeadScoreBucket { score: number; n: number }
export interface LeadScoreResponse {
  buckets: LeadScoreBucket[]
  avg: number | null
  median: number | null
  total: number
}

export async function fetchLeadScore(range: DateRange, variant?: Variant, audience?: Audience): Promise<LeadScoreResponse> {
  const r = await fetch(`/api/admin/leadscore?${rangeParams(range, variant, audience)}`, { credentials: 'include' })
  if (!r.ok) throw new Error(`leadscore ${r.status}`)
  return r.json()
}

export interface GeoRow { postnr: string; by: string | null; n: number }
export interface GeoResponse { rows: GeoRow[]; total: number; withPostnr: number }

export async function fetchGeo(range: DateRange, variant?: Variant, limit = 25, audience?: Audience): Promise<GeoResponse> {
  const params = rangeParams(range, variant, audience)
  params.set('limit', String(limit))
  const r = await fetch(`/api/admin/geo?${params}`, { credentials: 'include' })
  if (!r.ok) throw new Error(`geo ${r.status}`)
  return r.json()
}

export interface AgeBucket { band: string; n: number }
export interface AgeResponse { buckets: AgeBucket[]; total: number }

export async function fetchAge(range: DateRange, variant?: Variant, audience?: Audience): Promise<AgeResponse> {
  const r = await fetch(`/api/admin/age?${rangeParams(range, variant, audience)}`, { credentials: 'include' })
  if (!r.ok) throw new Error(`age ${r.status}`)
  return r.json()
}

export interface InsuranceItem { insurance: string; n: number; pct: number }
export interface InsurancesResponse { items: InsuranceItem[]; total: number; multiInsuranceRate: number }

export async function fetchInsurances(range: DateRange, variant?: Variant, audience?: Audience): Promise<InsurancesResponse> {
  const r = await fetch(`/api/admin/insurances?${rangeParams(range, variant, audience)}`, { credentials: 'include' })
  if (!r.ok) throw new Error(`insurances ${r.status}`)
  return r.json()
}

export interface DayHourCell { dow: number; hour: number; n: number }
export interface DayHourResponse { cells: DayHourCell[]; max: number }

export async function fetchDayHour(range: DateRange, variant?: Variant, type: 'sessions' | 'leads' = 'sessions', audience?: Audience): Promise<DayHourResponse> {
  const params = rangeParams(range, variant, audience)
  params.set('type', type)
  const r = await fetch(`/api/admin/dayhour?${params}`, { credentials: 'include' })
  if (!r.ok) throw new Error(`dayhour ${r.status}`)
  return r.json()
}

export interface OpsResponse {
  zapier: { sent: number; pending: number; failed: number; deliveryRate: number }
  duplicates: { count: number; rate: number }
  validationRejects: { last24h: number; reasons: Array<{ reason: string; n: number }> }
}

export async function fetchOps(range: DateRange, variant?: Variant, audience?: Audience): Promise<OpsResponse> {
  const r = await fetch(`/api/admin/ops?${rangeParams(range, variant, audience)}`, { credentials: 'include' })
  if (!r.ok) throw new Error(`ops ${r.status}`)
  return r.json()
}

/* ─── v2.5 endpoints (segments, time-to-lead, snapshots) ─── */

export type SegmentDimension = 'device' | 'source' | 'age'

export interface SegmentCell {
  sessions: number
  leads: number
  rate: number
  ciLow: number
  ciHigh: number
}

export interface SegmentRow {
  name: string
  chat: SegmentCell
  calculator: SegmentCell
  delta: number
  pValue: number | null
}

export interface SegmentsResponse {
  dimension: SegmentDimension
  segments: SegmentRow[]
  paradox: boolean
}

export async function fetchSegments(range: DateRange, dimension: SegmentDimension = 'device', audience?: Audience): Promise<SegmentsResponse> {
  const params = new URLSearchParams({
    from: String(range.from),
    to: String(range.to),
    dimension,
  })
  if (audience) params.set('audience', audience)
  const r = await fetch(`/api/admin/segments?${params}`, { credentials: 'include' })
  if (!r.ok) throw new Error(`segments ${r.status}`)
  return r.json()
}

export interface TimeToLeadBucket { label: string; lowMs: number; highMs: number; n: number }
export interface TimeToLeadResponse {
  buckets: TimeToLeadBucket[]
  median: number | null
  p90: number | null
  total: number
  measurable: number
  unknown: number
}

export async function fetchTimeToLead(range: DateRange, variant?: Variant, audience?: Audience): Promise<TimeToLeadResponse> {
  const r = await fetch(`/api/admin/timetolead?${rangeParams(range, variant, audience)}`, { credentials: 'include' })
  if (!r.ok) throw new Error(`timetolead ${r.status}`)
  return r.json()
}

export interface SnapshotRow {
  name: string
  savedAt: number
  range: DateRange
  audience: Audience | 'all'
  variant: Variant | 'all'
  sessions: number
  leads: number
  completionRate: number
}

export async function fetchSnapshots(): Promise<{ snapshots: SnapshotRow[] }> {
  const r = await fetch('/api/admin/snapshots', { credentials: 'include' })
  if (!r.ok) throw new Error(`snapshots ${r.status}`)
  return r.json()
}

export interface LiveSession {
  id: string
  audience: 'unge' | 'aeldre'
  variant: 'chat' | 'calculator'
  lastStep: string
  secondsAgo: number
  secondsInFlow: number
}
export interface LiveResponse {
  sessions: LiveSession[]
  count: number
  activeThresholdMs: number
  now: number
}

export async function fetchLive(): Promise<LiveResponse> {
  const r = await fetch('/api/admin/live', { credentials: 'include' })
  if (!r.ok) throw new Error(`live ${r.status}`)
  return r.json()
}

export async function createSnapshot(
  name: string,
  range: DateRange,
  variant?: Variant,
  audience?: Audience,
): Promise<{ ok: true; snapshot: SnapshotRow }> {
  // Backend's parseRange reads from query params; sender vi audience+variant
  // med så snapshotrækken bærer hvilket filter den blev gemt under.
  const r = await fetch(`/api/admin/snapshot?${rangeParams(range, variant, audience)}`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ name }),
  })
  if (!r.ok) throw new Error(`snapshot ${r.status}`)
  return r.json()
}

/* ─── CSV export URLs (open via <a download>) ─── */

export function acquisitionCsvUrl(range: DateRange, variant?: Variant, audience?: Audience): string {
  return `/api/admin/acquisition.csv?${rangeParams(range, variant, audience)}`
}

export function geoCsvUrl(range: DateRange, variant?: Variant, limit = 100, audience?: Audience): string {
  const p = rangeParams(range, variant, audience)
  p.set('limit', String(limit))
  return `/api/admin/geo.csv?${p}`
}

export function leadScoreCsvUrl(range: DateRange, variant?: Variant, audience?: Audience): string {
  return `/api/admin/leadscore.csv?${rangeParams(range, variant, audience)}`
}
